import React, { useState, useMemo, useEffect } from 'react';
import useNewTracking from 'components/Globals/Analytics';
import { COMPONENTS, GOOGLE_OLD_TRACKING_SERVICES, SECTIONS } from 'components/Globals/Analytics/constants';
import Modal from 'components/uiLibrary/Modal';
import Loader from 'components/uiLibrary/Loader';
import Typography from 'components/uiLibrary/Typography';
import LinkButton from 'components/uiLibrary/LinkButton';
import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import ImageAvatar from 'components/uiLibrary/ImageAvatar';
import Checkbox from 'components/uiLibrary/Inputs/Checkbox';

import { useCreateProfileAccessMutation } from 'containers/Accounts/queries';

import { TP, VALID_AGENCY_STATUSES, PROFILE_ACCESS_REQUEST_STATUSES, PROFILE_TYPE_IDS } from 'constants/index';

import { useDialogs } from 'utils/hooks/useDialogs';
import usePageContext from 'utils/hooks/usePageContext';
import { getMediaSource } from 'utils/media';
import { TRACK_EVENTS, ARTIST_REQUEST_ACCESS_IDS } from 'utils/tracking';
import { submitHubspotForm } from 'utils/formUtils';
import useRouteChangeHandler from 'utils/hooks/useRouteChangeHandler';
import { useAccessPendingProfileIds } from 'utils/hooks/useAuthenticatedUser';

import { useTranslation, i18n, Trans } from 'src/i18n';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import classes from './RequestAccessModal.module.scss';

const trackingData = {
  section: SECTIONS.REQUEST_ACCESS_MODAL,
};
const RequestAccess = ({ entity, agencyDetails, userData, userProfiles, setIsSubmitted, isLoading, setIsLoading }) => {
  const { t } = useTranslation();
  const track = useNewTracking();

  const [isAgreeTerms, setIsAgreeTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [agencyProfile, agency] = useMemo(() => {
    const agencyProfileInfo = userProfiles?.find(
      profile =>
        profile?.profileType?.id === PROFILE_TYPE_IDS.AGENT &&
        profile?.agencies?.some(agencyInfo => VALID_AGENCY_STATUSES.includes(agencyInfo?.status)),
    );

    return [
      agencyProfileInfo,
      agency
        ? agencyDetails
        : agencyProfileInfo?.agencies?.find(agencyInfo => VALID_AGENCY_STATUSES.includes(agencyInfo?.status)),
    ];
  }, [userProfiles, agencyDetails]);

  const submitDataToHbspt = () => {
    const portalId = process.env.HUBSPOT_REGISTER_KEY;
    const env = process.env.APP_ENV;
    const formId =
      env === 'production' ? 'bdb68376-c7e0-4906-bf2e-6f015e0a7564' : '5accccd7-cf62-45a6-b48e-c138aa4c1482';
    const payload = {
      fields: [
        { name: 'email', value: userData.email },
        { name: 'hs_language', value: i18n?.language },
        { name: 'entity_share_access', value: entity?.name || '' },
        { name: 'entity_id_share_access', value: entity?.id || '' },
        { name: 'user_id__database_', value: userData.id || '' },
      ],
    };

    submitHubspotForm(portalId, formId, payload);
  };

  const { mutate: createProfileAccessMutation } = useCreateProfileAccessMutation({
    onSuccess: res => {
      submitDataToHbspt();
      setIsLoading(false);
      track.click(
        {
          name: TRACK_EVENTS.CLICK,
          id: ARTIST_REQUEST_ACCESS_IDS.REQUEST_ACCESS,
        },
        GOOGLE_OLD_TRACKING_SERVICES,
      );
      const profileAccessStatus = res?.data?.status;
      if (profileAccessStatus === PROFILE_ACCESS_REQUEST_STATUSES.PENDING) {
        setIsSubmitted(true);
      } else {
        setIsLoading(true);
        window.location.reload();
      }
    },
    onError: error => {
      setErrorMessage(error.message);
      setIsLoading(false);
    },
  });

  const handleRequestAccess = () => {
    const requestData = {
      sender: {
        user: userData,
        profile: agencyProfile,
        email: userData.email,
      },
      message: `${entity.name}`,
      agency,
      profile: entity,
    };

    setIsLoading(true);
    createProfileAccessMutation(requestData);
  };

  useRouteChangeHandler(setIsLoading);

  const imageSrc = getMediaSource({
    id: entity?.id,
    file: { urls: entity?.image || entity?.logo },
  });

  return (
    <div>
      <div className={classes.requestAccess__container}>
        <div className={classes.requestAccess__profileContainer}>
          {imageSrc ? (
            <Image
              useIntersectionObserver
              disableNextImage
              alt={entity?.name}
              src={imageSrc}
              width={42}
              height={60}
              transformations={TRANSFORMATIONS.PROFILE_IMAGE}
            />
          ) : (
            <ImageAvatar type="V4" name={entity?.name} width={42} height={60} fontSize={20} shape="rect" />
          )}
          <div className={classes.requestAccess__profileContent}>
            <Typography variant="h5" weight="medium">
              {entity?.name}
            </Typography>
            <Typography size={12} color="secondary">
              {entity?.headline}
            </Typography>
          </div>
        </div>
        <div className={classes.requestAccess__message}>
          <Typography size="14" weight="medium">
            {t('GET_ACCESS_TO_ARTIST_PROFILE')}
          </Typography>
        </div>
        <Checkbox
          className={classes.requestAccess__checkbox}
          value={isAgreeTerms}
          checked={isAgreeTerms}
          onChange={e => setIsAgreeTerms(e?.target?.checked)}
          label={
            <Typography size="12" color="secondary">
              {t('AGENCY_ACCESS_REQUEST_CHECKBOX_LABEL')}
            </Typography>
          }
          size="small"
        />
        {errorMessage && (
          <Typography size={12} className={classes.error}>
            {errorMessage}
          </Typography>
        )}
      </div>
      <div className={classes.requestAccess__footer}>
        <LinkButton
          variant="primary"
          shape="rectangle"
          styles={{ root: classes.requestAccess__primaryCTA }}
          onClick={handleRequestAccess}
          disabled={!isAgreeTerms || isLoading}
          trackingData={{ component: COMPONENTS.SUBMIT_CTA, ...trackingData }}
        >
          {t(`${TP}.ACCESS_REQUEST_TITLE`)}
        </LinkButton>
      </div>
    </div>
  );
};

const RequestAccessSuccess = ({ entity, isSubmitted, isRequestAccessPending, handleClose, setIsLoading }) => {
  const { t } = useTranslation();

  const onClose = () => {
    if (isSubmitted) {
      setIsLoading(true);
      window.location.reload();
    } else {
      handleClose();
    }
  };

  return (
    <>
      <div className={classes.requestAccessSuccess}>
        <div className={classes.requestAccessSuccess__icon}>
          <SpriteIcon icon={isSubmitted ? 'check_circle' : 'hour_glass'} size={28} />
        </div>
        <Typography size={16} weight="medium">
          {isSubmitted
            ? t('REQUEST_PROFILE_ACCESS_SUCCESS', { profileName: entity?.name })
            : t('REQUEST_ACCESS_ALREADY_SENT', { profileName: entity?.name })}
        </Typography>
        {isRequestAccessPending && (
          <div className={classes.requestAccessSuccess__contact}>
            <Typography size={16}>
              <Trans
                i18nKey="REQUEST_ACCESS_CONTACT_MESSAGE"
                // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label
                components={{ email: <a href="mailto:contact@operabase.com" /> }}
              />
            </Typography>
          </div>
        )}
      </div>
      <div className={classes.requestAccess__footer}>
        <LinkButton
          variant="primary"
          shape="rectangle"
          styles={{ root: classes.requestAccess__primaryCTA }}
          onClick={onClose}
        >
          {t('OKAY')}
        </LinkButton>
      </div>
    </>
  );
};

const RequestAccessModal = ({ isOpen, targetProfile, agencyDetails, onClose = () => {} }) => {
  const { t } = useTranslation();
  const track = useNewTracking();
  const { entity, permissions } = usePageContext();
  const { userData, userProfiles } = permissions;
  const profileIdsWithAccess = useAccessPendingProfileIds();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { isRequestAccessModalOpen, setIsRequestAccessModalOpen } = useDialogs();
  const [isLoading, setIsLoading] = useState(false);
  const isRequestAccessPending = profileIdsWithAccess?.includes(entity?.id || targetProfile?.id);

  const handleClose = () => {
    setIsRequestAccessModalOpen(false);
    setIsRequestAccessModalOpen(false);
    setIsSubmitted(false);
    onClose();
  };

  useEffect(() => {
    track.click(
      {
        name: TRACK_EVENTS.VIEW_ARTIST_REQUEST_ACCESS_MODAL,
      },
      GOOGLE_OLD_TRACKING_SERVICES,
    );
  }, [track]);

  return (
    <Modal
      isOpen={isRequestAccessModalOpen || isOpen}
      onClose={handleClose}
      styles={{
        modalContainer: classes.requestAccess__modalContainer,
      }}
      allowMobileDrawer
      title={isSubmitted ? t('REQUEST_ACCESS_SENT') : t(`${TP}.ACCESS_REQUEST_TITLE`)}
    >
      {isSubmitted || isRequestAccessPending ? (
        <RequestAccessSuccess
          entity={entity}
          isSubmitted={isSubmitted}
          isRequestAccessPending={isRequestAccessPending}
          handleClose={handleClose}
          setIsLoading={setIsLoading}
          trackingData={trackingData}
        />
      ) : (
        <RequestAccess
          entity={targetProfile ?? entity}
          agencyDetails={agencyDetails}
          userData={userData}
          userProfiles={userProfiles}
          setIsSubmitted={setIsSubmitted}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          trackingData={trackingData}
        />
      )}
      {isLoading && (
        <div className={classes.loader}>
          <Loader isLoading={isLoading} />
        </div>
      )}
    </Modal>
  );
};

export default RequestAccessModal;
